import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { BuildingSavingsDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/utils";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { useNumberFormatter } from "web_myplann/Shared/Hooks/useNumberFormatter";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";
import { DataLineChart } from "../DataLineChart/DataLineChart";

export const BuildingSavings: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const formatNumber = useNumberFormatter();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const portfolioData = baseData.data as BuildingSavingsDetailDataDTO;

	const savings = portfolioData.payment
		? getPortfolioFrequencyValue(
				portfolioData.payment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const formattedAppreciation = formatNumber(portfolioData.appreciation);
	const appreciation = formattedAppreciation && `${formattedAppreciation} %`;

	const stateContribution =
		typeof portfolioData.stateContribution !== "boolean"
			? ""
			: t(Resources.Common[portfolioData.stateContribution ? "yes" : "no"]);

	return (
		<Stack>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalInvestedValue={totalInvestedValue}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={showChartInfo}
			/>

			{showChartInfo && (
				<>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.targetAmount)}
						value={moneyFormatter(
							portfolioData.targetAmount,
							currencySign,
							"",
							2,
						)}
					/>

					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}

			{daysData.length !== 0 && <SectionDivider />}

			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					<Stack>
						<BasicInfo data={baseData} />
						<DataNote
							label={t(Resources.PortfolioDetail.BuildingSavings.note)}
							text={baseData.note}
						/>
					</Stack>
				}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			>
				<Stack>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.name)}
						value={baseData.clientName}
					/>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.contractNumber)}
						value={baseData.contractNumber}
					/>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.producent)}
						value={baseData.producer}
					/>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.product)}
						value={baseData.product}
					/>
					<DataRow
						label={t(Resources.PortfolioDetail.BuildingSavings.savings)}
						value={savings}
					/>
					<DataRow
						label={t(Resources.PortfolioDetail.BuildingSavings.appreciation)}
						value={appreciation}
					/>
					<DataRow
						label={t(
							Resources.PortfolioDetail.BuildingSavings.stateContribution,
						)}
						value={stateContribution}
					/>
				</Stack>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
